import { useCallback, useEffect, useMemo, useState } from "react";
import { ClientApiError } from "../auth/api";
import {
  getAuditDetail,
  getAuditLogs,
  getFeatureFlags,
  getRoleMatrix,
  getSettings,
  patchFeatureFlag,
  patchSetting,
  type AuditView,
  type FeatureFlagView,
  type RoleView,
  type SettingView,
} from "../admin/api";
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Skeleton,
  Table,
  Textarea,
  Toast,
} from "../components/ui";

const dateFormatter = new Intl.DateTimeFormat("vi-VN", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Asia/Ho_Chi_Minh",
});

function errorInfo(error: unknown) {
  if (error instanceof ClientApiError) {
    return { message: error.problem.message, requestId: error.problem.requestId };
  }
  return { message: "Không thể tải dữ liệu quản trị.", requestId: undefined };
}

function PageHeader({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <header className="admin-page-header">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      <p>{description}</p>
    </header>
  );
}

export function AdminCoreDashboardPage() {
  return (
    <div className="admin-page-stack">
      <PageHeader
        eyebrow="Control plane"
        title="Vận hành có kiểm soát"
        description="Quyền, audit, cấu hình và feature flag đều được thực thi ở backend. Giao diện này chỉ phản ánh quyết định từ server."
      />
      <div className="admin-summary-grid">
        <article className="admin-summary-card">
          <Badge tone="success">Backend enforced</Badge>
          <h3>Role và permission</h3>
          <p>Permission theo module, action và scope own/kennel/assigned/global.</p>
        </article>
        <article className="admin-summary-card">
          <Badge tone="info">Append-only</Badge>
          <h3>Audit trail</h3>
          <p>Before/after được lọc trường nhạy cảm và truy vết bằng request ID.</p>
        </article>
        <article className="admin-summary-card">
          <Badge tone="warning">Versioned</Badge>
          <h3>Settings & flags</h3>
          <p>Validation server-side, optimistic concurrency và default an toàn.</p>
        </article>
      </div>
    </div>
  );
}

export function RoleMatrixPage() {
  const [roles, setRoles] = useState<RoleView[] | null>(null);
  const [error, setError] = useState<ReturnType<typeof errorInfo> | null>(null);
  const load = useCallback(() => {
    setError(null);
    getRoleMatrix().then((data) => setRoles(data.roles)).catch((reason) => setError(errorInfo(reason)));
  }, []);
  useEffect(load, [load]);

  if (error) return <ErrorState title="Không tải được ma trận quyền" description={error.message} requestId={error.requestId} action={{ label: "Thử lại", onClick: load }} />;
  if (!roles) return <Skeleton lines={8} />;

  const modules = [...new Set(roles.flatMap((role) => role.permissions.map((permission) => permission.module)))].sort();
  return (
    <div className="admin-page-stack">
      <PageHeader eyebrow="RBAC" title="Ma trận role và permission" description="Guest là policy role; các role còn lại được cấp cho user theo scope. Không có quyền nào được quyết định ở client." />
      <div className="role-priority-strip">
        {roles.map((role) => (
          <article key={role.id} className="role-priority-card">
            <strong>{role.name}</strong>
            <span>{role.code}</span>
            <Badge tone={role.code === "super_admin" ? "danger" : role.code === "admin" ? "warning" : "neutral"}>Priority {role.priority}</Badge>
          </article>
        ))}
      </div>
      <Table
        caption="Permission theo module"
        headers={["Module", ...roles.map((role) => role.name)]}
        rows={modules.map((module) => [
          <strong key={`${module}-label`}>{module}</strong>,
          ...roles.map((role) => {
            const permissions = role.permissions.filter((permission) => permission.module === module);
            return permissions.length ? (
              <div className="permission-list" key={`${module}-${role.id}`}>
                {permissions.map((permission) => (
                  <code key={permission.code}>{permission.action}:{permission.scope}</code>
                ))}
              </div>
            ) : <span key={`${module}-${role.id}`}>—</span>;
          }),
        ])}
      />
    </div>
  );
}

export function AuditLogPage() {
  const [items, setItems] = useState<AuditView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ReturnType<typeof errorInfo> | null>(null);
  const [detail, setDetail] = useState<AuditView | null>(null);

  const load = useCallback((nextCursor?: string | null, append = false) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ limit: "25" });
    if (action) params.set("action", action);
    if (entityType) params.set("entityType", entityType);
    if (nextCursor) params.set("cursor", nextCursor);
    getAuditLogs(params)
      .then((data) => {
        setItems((current) => append ? [...current, ...data.items] : data.items);
        setCursor(data.nextCursor);
      })
      .catch((reason) => setError(errorInfo(reason)))
      .finally(() => setLoading(false));
  }, [action, entityType]);

  useEffect(() => { load(null, false); }, [load]);

  const openDetail = (id: string) => {
    getAuditDetail(id).then((data) => setDetail(data.audit)).catch((reason) => setError(errorInfo(reason)));
  };

  return (
    <div className="admin-page-stack">
      <PageHeader eyebrow="Audit" title="Nhật ký thay đổi" description="Audit là append-only. Email, phone, password, token, secret và credential bị lọc trước khi ghi." />
      <div className="admin-filter-bar">
        <Input label="Action" value={action} onChange={(event) => setAction(event.target.value)} placeholder="settings.updated" />
        <Input label="Entity type" value={entityType} onChange={(event) => setEntityType(event.target.value)} placeholder="site_setting" />
        <Button onClick={() => load(null, false)}>Lọc</Button>
      </div>
      {error ? <ErrorState title="Không tải được audit" description={error.message} requestId={error.requestId} action={{ label: "Thử lại", onClick: () => load(null, false) }} /> : null}
      {loading && items.length === 0 ? <Skeleton lines={7} /> : null}
      {!loading && items.length === 0 ? <EmptyState title="Chưa có audit record" description="Các mutation nhạy cảm sẽ xuất hiện tại đây." /> : null}
      {items.length > 0 ? (
        <Table
          caption="Audit records"
          headers={["Thời gian", "Action", "Entity", "Request ID", "Chi tiết"]}
          rows={items.map((item) => [
            dateFormatter.format(new Date(item.createdAt)),
            <code key={`${item.id}-action`}>{item.action}</code>,
            `${item.entityType}${item.entityId ? ` · ${item.entityId}` : ""}`,
            <code key={`${item.id}-request`}>{item.requestId.slice(0, 16)}</code>,
            <Button key={`${item.id}-button`} variant="secondary" size="sm" onClick={() => openDetail(item.id)}>Xem</Button>,
          ])}
        />
      ) : null}
      {cursor ? <Button variant="secondary" disabled={loading} onClick={() => load(cursor, true)}>Tải thêm</Button> : null}
      <Dialog open={Boolean(detail)} title="Chi tiết audit" onClose={() => setDetail(null)}>
        {detail ? (
          <div className="audit-detail">
            <dl>
              <div><dt>Action</dt><dd>{detail.action}</dd></div>
              <div><dt>Entity</dt><dd>{detail.entityType} · {detail.entityId ?? "—"}</dd></div>
              <div><dt>Request ID</dt><dd><code>{detail.requestId}</code></dd></div>
              <div><dt>Thời gian</dt><dd>{dateFormatter.format(new Date(detail.createdAt))}</dd></div>
            </dl>
            <h3>Before</h3><pre>{JSON.stringify(detail.before, null, 2)}</pre>
            <h3>After</h3><pre>{JSON.stringify(detail.after, null, 2)}</pre>
            <h3>Metadata</h3><pre>{JSON.stringify(detail.metadata, null, 2)}</pre>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

function settingInputValue(setting: SettingView): string {
  if (setting.value === null || setting.value === undefined) return "";
  return setting.type === "json" ? JSON.stringify(setting.value, null, 2) : String(setting.value);
}

export function SettingsPage() {
  const [settings, setSettings] = useState<SettingView[] | null>(null);
  const [selected, setSelected] = useState<SettingView | null>(null);
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<ReturnType<typeof errorInfo> | null>(null);
  const [toast, setToast] = useState(false);

  const load = useCallback(() => {
    setError(null);
    getSettings().then((data) => setSettings(data.settings)).catch((reasonValue) => setError(errorInfo(reasonValue)));
  }, []);
  useEffect(load, [load]);

  const groups = useMemo(() => {
    const map = new Map<string, SettingView[]>();
    for (const setting of settings ?? []) map.set(setting.group, [...(map.get(setting.group) ?? []), setting]);
    return [...map.entries()];
  }, [settings]);

  const open = (setting: SettingView) => {
    setSelected(setting);
    setValue(setting.type === "secret_ref" ? setting.secretReference ?? "" : settingInputValue(setting));
    setReason("");
    setError(null);
  };

  const save = async () => {
    if (!selected) return;
    let parsed: unknown = value;
    if (selected.type === "boolean") parsed = value === "true";
    if (selected.type === "number") parsed = Number(value);
    if (selected.type === "json") {
      try { parsed = JSON.parse(value); } catch { setError({ message: "JSON không hợp lệ.", requestId: undefined }); return; }
    }
    try {
      const result = await patchSetting(selected.key, { value: parsed, expectedVersion: selected.version, reason });
      setSettings((current) => current?.map((item) => item.key === result.setting.key ? result.setting : item) ?? null);
      setSelected(null);
      setToast(true);
    } catch (reasonValue) { setError(errorInfo(reasonValue)); }
  };

  if (!settings && !error) return <Skeleton lines={8} />;
  return (
    <div className="admin-page-stack">
      <PageHeader eyebrow="Settings" title="Cấu hình hệ thống" description="Mọi giá trị được validate ở server và dùng version để tránh ghi đè. Secret chỉ hiển thị tên biến môi trường tham chiếu." />
      {error && !selected ? <ErrorState title="Không tải được cấu hình" description={error.message} requestId={error.requestId} action={{ label: "Thử lại", onClick: load }} /> : null}
      {groups.map(([group, groupSettings]) => (
        <section className="settings-group" key={group}>
          <h3>{group}</h3>
          <div className="settings-grid">
            {groupSettings.map((setting) => (
              <article className="setting-card" key={setting.key}>
                <div>
                  <code>{setting.key}</code>
                  <p>{setting.description}</p>
                </div>
                <div className="setting-value">
                  {setting.type === "secret_ref" ? <Badge tone="warning">Env: {setting.secretReference}</Badge> : <strong>{settingInputValue(setting)}</strong>}
                  <span>v{setting.version}</span>
                </div>
                <Button variant="secondary" size="sm" onClick={() => open(setting)}>Chỉnh sửa</Button>
              </article>
            ))}
          </div>
        </section>
      ))}
      <Dialog open={Boolean(selected)} title="Cập nhật cấu hình" onClose={() => setSelected(null)}>
        {selected ? (
          <div className="admin-dialog-form">
            <code>{selected.key}</code>
            {selected.type === "boolean" ? (
              <Select label="Giá trị" value={value} onChange={(event) => setValue(event.target.value)}>
                <option value="true">Bật</option><option value="false">Tắt</option>
              </Select>
            ) : selected.type === "json" ? (
              <Textarea label="Giá trị JSON" rows={8} value={value} onChange={(event) => setValue(event.target.value)} />
            ) : (
              <Input label={selected.type === "secret_ref" ? "Tên biến môi trường" : "Giá trị"} value={value} onChange={(event) => setValue(event.target.value)} readOnly={selected.type === "secret_ref"} />
            )}
            <Textarea label="Lý do thay đổi" value={reason} onChange={(event) => setReason(event.target.value)} />
            {error ? <ErrorState title="Không thể lưu" description={error.message} requestId={error.requestId} /> : null}
            <div className="button-row"><Button onClick={save}>Lưu phiên bản mới</Button><Button variant="ghost" onClick={() => setSelected(null)}>Hủy</Button></div>
          </div>
        ) : null}
      </Dialog>
      {toast ? <Toast title="Đã lưu" message="Cấu hình đã được version hóa và audit." onClose={() => setToast(false)} /> : null}
    </div>
  );
}

export function FeatureFlagsPage() {
  const [flags, setFlags] = useState<FeatureFlagView[] | null>(null);
  const [error, setError] = useState<ReturnType<typeof errorInfo> | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const load = useCallback(() => {
    setError(null);
    getFeatureFlags().then((data) => setFlags(data.flags)).catch((reason) => setError(errorInfo(reason)));
  }, []);
  useEffect(load, [load]);

  const update = async (flag: FeatureFlagView, patch: Partial<Pick<FeatureFlagView, "enabled" | "rolloutPercentage">>) => {
    setSavingId(flag.id);
    setError(null);
    try {
      const result = await patchFeatureFlag(flag.id, {
        enabled: patch.enabled ?? flag.enabled,
        rolloutPercentage: patch.rolloutPercentage ?? flag.rolloutPercentage,
        expectedVersion: flag.version,
      });
      setFlags((current) => current?.map((item) => item.id === result.flag.id ? result.flag : item) ?? null);
    } catch (reason) { setError(errorInfo(reason)); }
    finally { setSavingId(null); }
  };

  if (!flags && !error) return <Skeleton lines={8} />;
  return (
    <div className="admin-page-stack">
      <PageHeader eyebrow="Feature flags" title="Phát hành có kiểm soát" description="Flag được đánh giá ở server theo environment và audience. Khi tắt, API được bảo vệ vẫn bị chặn dù client cố gọi trực tiếp." />
      {error ? <ErrorState title="Không xử lý được feature flag" description={error.message} requestId={error.requestId} action={{ label: "Tải lại", onClick: load }} /> : null}
      <div className="flag-list">
        {(flags ?? []).map((flag) => (
          <article className="flag-card" key={flag.id}>
            <div><code>{flag.key}</code><p>{flag.description}</p></div>
            <div className="flag-meta"><Badge tone={flag.enabled ? "success" : "neutral"}>{flag.enabled ? "Đang bật" : "Đang tắt"}</Badge><span>{flag.environment} · {flag.audienceType}{flag.audienceValue ? `:${flag.audienceValue}` : ""}</span></div>
            <Input label="Rollout %" type="number" min={0} max={100} value={flag.rolloutPercentage} onChange={(event) => setFlags((current) => current?.map((item) => item.id === flag.id ? { ...item, rolloutPercentage: Number(event.target.value) } : item) ?? null)} />
            <div className="button-row">
              <Button variant={flag.enabled ? "danger" : "primary"} size="sm" disabled={savingId === flag.id} onClick={() => update(flag, { enabled: !flag.enabled })}>{flag.enabled ? "Tắt" : "Bật"}</Button>
              <Button variant="secondary" size="sm" disabled={savingId === flag.id} onClick={() => update(flag, {})}>Lưu rollout</Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
